import { useCallback, useEffect, useRef } from 'react';

import { resolveSceneNpcConfigs } from '../../game/sceneLoader';
import { getNpcConfigs, getOwnedUserNpcs } from '../../services/firebase/firestore';
import { useSimulationStore } from '../../stores/simulationStore';
import { defaultNpcs } from '../../services/npcFallback';
import type { NpcConfig, NpcDefinition, UserNpcConfig } from '../../types/npc';
import type { SceneDocument } from '../../types/scene';
import { mergeScopedSimulationNpcConfigs } from './useNpcCatalogShared';

interface UseNpcCatalogArgs {
  isAuthBypass: boolean;
  setNpcConfigs: (npcs: NpcConfig[]) => void;
}

interface UseNpcCatalogValue {
  applyOptimisticUserNpcToScene: (scene: SceneDocument, userNpc: UserNpcConfig) => void;
  refreshNpcConfigs: (uid: string | null, scene: SceneDocument | null) => Promise<void>;
}

export const useNpcCatalog = ({
  isAuthBypass,
  setNpcConfigs,
}: UseNpcCatalogArgs): UseNpcCatalogValue => {
  const npcRefreshRequestIdRef = useRef(0);
  const npcDefinitionCatalogRef = useRef<NpcDefinition[]>(defaultNpcs);
  const currentSceneRef = useRef<SceneDocument | null>(null);
  const lastAppliedNpcSignatureRef = useRef<string | null>(null);
  const activeSimulation = useSimulationStore((state) => state.activeSimulation);
  const activeSimulationRef = useRef(activeSimulation);
  activeSimulationRef.current = activeSimulation;

  const applyResolvedNpcConfigs = useCallback(
    (nextNpcConfigs: NpcConfig[]): void => {
      const nextSignature = JSON.stringify(nextNpcConfigs);
      if (nextSignature === lastAppliedNpcSignatureRef.current) {
        return;
      }
      lastAppliedNpcSignatureRef.current = nextSignature;
      setNpcConfigs(nextNpcConfigs);
    },
    [setNpcConfigs],
  );

  const applySceneNpcConfigs = useCallback(
    (scene: SceneDocument | null, npcCatalog: NpcDefinition[]): void => {
      currentSceneRef.current = scene;
      if (!scene) {
        applyResolvedNpcConfigs([]);
        return;
      }
      const resolvedNpcConfigs = resolveSceneNpcConfigs(scene, npcCatalog);
      applyResolvedNpcConfigs(
        mergeScopedSimulationNpcConfigs({
          baseNpcConfigs: resolvedNpcConfigs,
          scene,
          activeSimulation: activeSimulationRef.current,
        }),
      );
    },
    [applyResolvedNpcConfigs],
  );

  useEffect(() => {
    if (!currentSceneRef.current) {
      return;
    }
    applySceneNpcConfigs(currentSceneRef.current, npcDefinitionCatalogRef.current);
  }, [activeSimulation, applySceneNpcConfigs]);

  const applyOptimisticUserNpcToScene = useCallback(
    (scene: SceneDocument, userNpc: UserNpcConfig): void => {
      const mergedById = new Map<string, NpcDefinition>();
      npcDefinitionCatalogRef.current.forEach((npc) => mergedById.set(npc.id, npc));
      mergedById.set(userNpc.id, userNpc);
      npcDefinitionCatalogRef.current = [...mergedById.values()];
      applySceneNpcConfigs(scene, npcDefinitionCatalogRef.current);
    },
    [applySceneNpcConfigs],
  );

  const refreshNpcConfigs = useCallback(
    async (uid: string | null, scene: SceneDocument | null): Promise<void> => {
      const requestId = npcRefreshRequestIdRef.current + 1;
      npcRefreshRequestIdRef.current = requestId;

      if (scene) {
        applySceneNpcConfigs(scene, npcDefinitionCatalogRef.current);
      } else {
        applyResolvedNpcConfigs([]);
      }

      if (isAuthBypass) {
        if (requestId !== npcRefreshRequestIdRef.current) {
          return;
        }
        npcDefinitionCatalogRef.current = defaultNpcs;
        applySceneNpcConfigs(scene, defaultNpcs);
        return;
      }

      let npcs: NpcDefinition[] = [];
      try {
        const loaded = await Promise.resolve(getNpcConfigs(uid ?? undefined));
        npcs = Array.isArray(loaded) ? loaded : [];
      } catch {
        npcs = [];
      }

      let ownerUserNpcConfigs: NpcDefinition[] = [];
      const hasSceneUserPlacements =
        scene?.npcPlacements.some((placement) => placement.source === 'user') ?? false;
      const shouldIncludeOwnedUserNpcs =
        Boolean(uid) && (scene?.ownerId === uid || hasSceneUserPlacements);
      if (uid && shouldIncludeOwnedUserNpcs) {
        try {
          const owned = await Promise.resolve(getOwnedUserNpcs(uid));
          ownerUserNpcConfigs = (Array.isArray(owned) ? owned : []).map((npc) => ({
            ...npc,
            id: `user:${npc.id}`,
            source: 'user',
            sourceNpcId: npc.id,
            spriteId: npc.spriteId || 'npc_user',
          }));
        } catch {
          ownerUserNpcConfigs = [];
        }
      }

      if (requestId !== npcRefreshRequestIdRef.current) {
        return;
      }

      const baseCatalog = npcs.length === 0 ? defaultNpcs : npcs;
      const mergedById = new Map<string, NpcDefinition>();
      baseCatalog.forEach((npc) => mergedById.set(npc.id, npc));
      ownerUserNpcConfigs.forEach((npc) => mergedById.set(npc.id, npc));
      npcDefinitionCatalogRef.current = [...mergedById.values()];
      applySceneNpcConfigs(scene, npcDefinitionCatalogRef.current);
    },
    [applyResolvedNpcConfigs, applySceneNpcConfigs, isAuthBypass],
  );

  return {
    applyOptimisticUserNpcToScene,
    refreshNpcConfigs,
  };
};
